/**
 * Thin wrapper around livekit-client's Room. Handles:
 *   - Connect with a token fetched from the backend
 *   - Publish local audio track with master plan §3.1 mic constraints
 *   - Subscribe to remote audio (the Vella agent's TTS output)
 *   - Expose event subscriptions for participant/track/data events
 *
 * Usage:
 *   const client = createLiveKitClient();
 *   await client.connect({ token, url });
 *   await client.publishMicrophone();
 *   client.on('agentAudio', (track) => playAudioTrack(track));
 *   client.on('dataMessage', (msg) => ...);     // from Phase 4 VAD/turn signals
 *   // Phase 5 §3.2 — event_id-gated audio queue:
 *   client.onAgentAudioChunk({ audio, event_id, alignment });
 *   client.bumpInterruptTimestamp(lastPlayedEventId);   // on barge-in
 *   await client.disconnect();
 *
 * This is an IMPERATIVE API — NOT a React hook. The component decides when
 * to call it and manages its own lifecycle (useEffect cleanup, user toggles).
 */
import {
  Room,
  RoomEvent,
  Track,
  createLocalTracks,
} from 'livekit-client';
import { createAudioQueue } from './audioQueue';

/**
 * Mic constraints — VELLA_2_0_MASTER_BUILD_PLAN.md §3.1 — copy verbatim.
 * Don't paraphrase, don't "improve". These are the same constraints
 * ElevenLabs Conversational AI uses and give us production-grade
 * AEC / noise suppression / AGC for free via WebRTC.
 */
const MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: { ideal: 1 },
    voiceIsolation: true,  // Chrome-specific
  }
};

// Events the wrapper emits to subscribers. Keep this list in sync with the
// JSDoc above so consumers know what they can subscribe to.
const CLIENT_EVENTS = new Set([
  'agentAudio',            // (RemoteAudioTrack) — agent's TTS playback track
  'agentConnected',        // (RemoteParticipant)
  'agentDisconnected',     // (RemoteParticipant)
  'dataMessage',           // ({ payload: Uint8Array, participant, topic }) — Phase 4 VAD/turn signals
  'transcription',         // ({ role: "user"|"assistant", segments: TranscriptionSegment[] })
                           //   Higher-level surface for chat-thread inserts. Role is inferred
                           //   from whether the segment came from the local participant
                           //   (user mic) or a remote participant (the agent).
  'audioChunkDropped',     // (event_id: number) — Phase 5 §3.2: dropped by event_id gate
  'audioChunkStarted',     // (event_id: number)
  'audioChunkEnded',       // (event_id: number)
  'connectionStateChanged',// (ConnectionState)
  'error',                 // (Error)
]);

// Topic / type used for the event_id-carrying audio sidecar on the
// LiveKit DataChannel. Phase 5 scaffolds the listener; full wire-up to
// Agent F / flashStream happens in the integration phase.
const AUDIO_CHUNK_DATA_TYPE = 'audio_chunk';

export function createLiveKitClient() {
  /** @type {Room | null} */
  let room = null;

  /** @type {Map<string, Set<Function>>} */
  const listeners = new Map();

  /** @type {import('livekit-client').LocalTrack[]} */
  let publishedLocalTracks = [];

  // Phase 5 §3.2 — event-id-gated audio queue for per-chunk TTS output.
  // This is separate from the continuous LiveKit `agentAudio` track; the
  // queue handles the sidecar path where each chunk carries an event_id.
  //
  // Lazily created on first use so a client that never receives chunked
  // audio doesn't spin up an <audio> element unnecessarily.
  let audioQueue = null;
  function ensureAudioQueue() {
    if (audioQueue) return audioQueue;
    audioQueue = createAudioQueue({
      onChunkDropped: (id) => emit('audioChunkDropped', id),
      onChunkStarted: (id) => emit('audioChunkStarted', id),
      onChunkEnded: (id) => emit('audioChunkEnded', id),
      onError: (err) => emit('error', err),
    });
    return audioQueue;
  }

  function emit(event, ...args) {
    const set = listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try { handler(...args); }
      catch (err) { console.warn('[livekitClient] listener threw for', event, err); }
    }
  }

  function on(event, handler) {
    if (!CLIENT_EVENTS.has(event)) {
      console.warn('[livekitClient] unknown event:', event);
      return;
    }
    if (typeof handler !== 'function') return;
    let set = listeners.get(event);
    if (!set) { set = new Set(); listeners.set(event, set); }
    set.add(handler);
  }

  function off(event, handler) {
    const set = listeners.get(event);
    if (!set) return;
    set.delete(handler);
  }

  function ensureRoom() {
    if (room) return room;
    room = new Room({
      adaptiveStream: true,
      dynacast: false,      // Phase 2 — keep simple; no video anyway
      // WebRTC AEC/NS/AGC on the capture side lives in the track constraints
      // above, not in Room options. Keeping RoomOptions minimal.
    });

    // Wire internal Room events → our external API.
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      emit('connectionStateChanged', state);
    });

    room.on(RoomEvent.ParticipantConnected, (participant) => {
      // Agent-side identity is opaque here; we surface every remote
      // participant as "agent" for now (Phase 2 has 1 agent per room).
      emit('agentConnected', participant);
    });

    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      emit('agentDisconnected', participant);
    });

    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (track.kind === Track.Kind.Audio) {
        emit('agentAudio', track, participant);
      }
    });

    room.on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
      emit('dataMessage', { payload, participant, topic });

      // Phase 5 sidecar parse: backend (Agent F / Agent P flashStream) will
      // publish `{ type: 'audio_chunk', event_id, alignment, audio }`
      // messages on the data channel. For NOW the backend doesn't emit this
      // yet — the parse is defensive and silently ignores malformed / other
      // messages. Full wiring happens in the integration phase.
      const parsed = tryParseAudioChunkMessage(payload);
      if (parsed) {
        ensureAudioQueue().push(parsed);
      }
    });

    // LiveKit's transcription channel — higher-level than DataReceived.
    // The agent SDK publishes user STT and agent TTS transcripts here as
    // TranscriptionSegment[]. Role is inferred from the participant identity:
    // local participant ⇒ "user", anyone else ⇒ "assistant" (the Vella
    // voice agent). Consumers can render finals into the chat thread and
    // optionally show interims as a typing indicator.
    room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
      try {
        const isLocal = participant && room.localParticipant
          ? participant.identity === room.localParticipant.identity
          : false;
        emit('transcription', {
          role: isLocal ? 'user' : 'assistant',
          segments: Array.isArray(segments) ? segments : [],
          participant,
        });
      } catch (err) {
        console.warn('[livekitClient] TranscriptionReceived handler threw:', err);
      }
    });

    room.on(RoomEvent.Disconnected, (reason) => {
      emit('connectionStateChanged', 'disconnected');
      if (reason !== undefined && reason !== null) {
        // Expose structured reason via error event so UI can decide to reconnect.
        emit('error', Object.assign(new Error('LiveKit disconnected'), { reason }));
      }
    });

    return room;
  }

  async function connect({ token, url }) {
    if (!token || !url) {
      throw new Error('[livekitClient] connect requires { token, url }');
    }
    const r = ensureRoom();
    try {
      await r.connect(url, token);
    } catch (err) {
      emit('error', err);
      throw err;
    }
    return r;
  }

  async function publishMicrophone() {
    if (!room || room.state !== 'connected') {
      throw new Error('[livekitClient] publishMicrophone called before connect');
    }
    let tracks;
    try {
      tracks = await createLocalTracks(MIC_CONSTRAINTS);
    } catch (err) {
      emit('error', err);
      throw err;
    }
    for (const track of tracks) {
      try {
        await room.localParticipant.publishTrack(track);
        publishedLocalTracks.push(track);
      } catch (err) {
        emit('error', err);
        throw err;
      }
    }
    return publishedLocalTracks;
  }

  async function disconnect() {
    // Stop + unpublish local tracks first (mic LED turns off immediately).
    for (const track of publishedLocalTracks) {
      try { track.stop(); } catch (_) { /* ignore */ }
    }
    publishedLocalTracks = [];

    if (audioQueue) {
      try { audioQueue.destroy(); } catch (_) { /* ignore */ }
      audioQueue = null;
    }

    if (room) {
      try { await room.disconnect(); }
      catch (err) { emit('error', err); }
      room = null;
    }
  }

  function getRoom() { return room; }

  // ── Phase 5 §3.2: event_id-gated audio queue surface ─────────────────
  // Consumers (TalkToVella.jsx, integration tests) invoke these either
  // directly (when audio chunks arrive out-of-band) or indirectly via the
  // DataReceived listener above. Full wiring is an integration-phase task.

  /**
   * Route an inbound agent audio chunk through the event_id-gated queue.
   * @param {{ audio: Blob|ArrayBuffer|Uint8Array, event_id: number, is_filler?: boolean, alignment?: object }} chunk
   */
  function onAgentAudioChunk(chunk) {
    ensureAudioQueue().push(chunk);
  }

  /**
   * Bump the barge-in gate. Phase 5 §3.2: any queued chunk with
   * event_id <= eventId is dropped; future pushes below the gate drop too.
   * @param {number} eventId
   */
  function bumpInterruptTimestamp(eventId) {
    ensureAudioQueue().bumpInterruptTimestamp(eventId);
  }

  /** Clear audio queue and stop any in-flight chunk. */
  function clearAudioQueue() {
    if (!audioQueue) return;
    audioQueue.clear();
  }

  /** Highest event_id that has begun playback (or -1). Phase 6 played_chars hook. */
  function getPlayedEventId() {
    if (!audioQueue) return -1;
    return audioQueue.getPlayedEventId();
  }

  // ── Phase 6U §1.3: upstream data-channel send (client_played_chars_report) ─
  // Thin forwarder to LocalParticipant.publishData so that
  // `src/lib/playedCharsReporter.js` — and any future upstream events —
  // can emit JSON without reaching into the Room directly.
  //
  // Returns the publishData Promise on success (so callers can await) or a
  // rejected Promise if we're not connected / serialization fails. Best-effort:
  // we don't want a dropped report to crash the UI.
  function sendData(payload) {
    if (!room || room.state !== 'connected' || !room.localParticipant) {
      return Promise.reject(new Error('[livekitClient] sendData before connect'));
    }
    let bytes;
    try {
      const text = typeof payload === 'string' ? payload : JSON.stringify(payload);
      bytes = new TextEncoder().encode(text);
    } catch (err) {
      return Promise.reject(err);
    }
    try {
      return room.localParticipant.publishData(bytes, { reliable: true });
    } catch (err) {
      return Promise.reject(err);
    }
  }

  return {
    connect,
    publishMicrophone,
    disconnect,
    on,
    off,
    getRoom,
    // Phase 5 §3.2 — audio queue API
    onAgentAudioChunk,
    bumpInterruptTimestamp,
    clearAudioQueue,
    getPlayedEventId,
    // Phase 6U §1.3 — upstream data-channel send
    sendData,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Data-channel sidecar parsing (Phase 5 §3.2)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Attempt to decode a LiveKit DataChannel payload as an audio_chunk sidecar
 * message. Returns null for anything that isn't one so callers can fall
 * through to other message handlers.
 *
 * The on-the-wire format (contract with Agent F / Agent P flashStream) is:
 *   JSON: { "type": "audio_chunk", "event_id": <number>, "alignment": {...},
 *           "is_filler": <bool>, "audio_base64": "<base64 bytes>" }
 * OR a binary framing variant that we don't implement yet — Phase 5 takes
 * the JSON path for simplicity; the backend isn't emitting either yet.
 *
 * @param {Uint8Array} payload
 * @returns {{ audio: Uint8Array, event_id: number, alignment?: object, is_filler?: boolean } | null}
 */
function tryParseAudioChunkMessage(payload) {
  if (!payload || typeof payload.byteLength !== 'number') return null;
  let text;
  try {
    text = new TextDecoder('utf-8', { fatal: false }).decode(payload);
  } catch (_) {
    return null;
  }
  if (!text || text[0] !== '{') return null;   // cheap non-JSON shortcut
  let obj;
  try { obj = JSON.parse(text); }
  catch (_) { return null; }
  if (!obj || obj.type !== AUDIO_CHUNK_DATA_TYPE) return null;
  if (typeof obj.event_id !== 'number') return null;
  if (typeof obj.audio_base64 !== 'string') return null;
  let audio;
  try { audio = base64ToUint8Array(obj.audio_base64); }
  catch (_) { return null; }
  return {
    audio,
    event_id: obj.event_id,
    alignment: obj.alignment,
    is_filler: !!obj.is_filler,
  };
}

function base64ToUint8Array(b64) {
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  // Node fallback (tests only — browsers have atob).
  if (typeof Buffer !== 'undefined') {
    const buf = Buffer.from(b64, 'base64');
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  throw new Error('no base64 decoder available');
}
